// src/components/Groups/UserSearchInput.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { userSearchAPI } from '../../api/groupsApi';
import { X, Search, User, Loader } from 'lucide-react';
import './UserSearchInput.css';

/**
 * Telegram-style multi-user search input.
 * Props:
 *   selectedUsers   – array of user objects already chosen
 *   onAdd(user)     – called when a user chip is added
 *   onRemove(user)  – called when a chip is removed
 *   excludeIds      – array of user IDs to exclude from results
 *   placeholder
 */
const UserSearchInput = ({
  selectedUsers = [],
  onAdd,
  onRemove,
  excludeIds = [],
  placeholder = 'Search users by name or email…',
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const data = await userSearchAPI.search(q);
      const selectedIds = new Set([
        ...selectedUsers.map((u) => String(u.id)),
        ...excludeIds.map(String),
      ]);
      setResults(data.filter((u) => !selectedIds.has(String(u.id))));
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [selectedUsers, excludeIds]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (user) => {
    onAdd(user);
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && query === '' && selectedUsers.length > 0) {
      onRemove(selectedUsers[selectedUsers.length - 1]);
    }
  };

  return (
    <div ref={containerRef} className="user-search-input">
      {/* Chips + input row */}
      <div
        className="user-search-input__field"
        onClick={() => inputRef.current?.focus()}
      >
        {selectedUsers.map((user) => (
          <span key={user.id} className="user-chip">
            <span className="user-chip__avatar">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt={user.full_name} />
              ) : (
                <User size={12} />
              )}
            </span>
            <span className="user-chip__name">{user.full_name || user.email}</span>
            <button
              type="button"
              className="user-chip__remove"
              onClick={(e) => { e.stopPropagation(); onRemove(user); }}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <div className="user-search-input__input-wrap">
          {loading ? (
            <Loader size={14} className="user-search-input__icon spinning" />
          ) : (
            <Search size={14} className="user-search-input__icon" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && setOpen(true)}
            placeholder={selectedUsers.length === 0 ? placeholder : ''}
            className="user-search-input__text"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <ul className="user-search-input__dropdown">
          {results.map((user) => (
            <li
              key={user.id}
              className="user-search-input__option"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(user); }}
            >
              <div className="user-search-input__option-avatar">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.full_name} />
                ) : (
                  <User size={16} />
                )}
              </div>
              <div className="user-search-input__option-info">
                <span className="user-search-input__option-name">
                  {user.full_name || `${user.first_name} ${user.last_name}`.trim()}
                </span>
                <span className="user-search-input__option-email">{user.email}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {open && !loading && query.length >= 2 && results.length === 0 && (
        <div className="user-search-input__empty">No users found</div>
      )}
    </div>
  );
};

export default UserSearchInput;
